<template>
  <div class="pack-import">
    <!-- Загрузка файла (без planfix_pack_id) -->
    <b-card v-if="!draft && !isPreparing" title="Импорт пакета в хранилище">
      <b-alert :show="Boolean(prepareError)" variant="danger" class="p-1">{{ prepareError }}</b-alert>

      <b-form @submit.prevent="uploadFile">
        <b-form-group label="Файл пакета (.siq)">
          <b-form-file
            v-model="siqFile"
            accept=".siq,.zip"
            placeholder="Выберите .siq файл"
            drop-placeholder="Перетащите файл сюда"
            browse-text="Обзор"
          />
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="!siqFile || !canManage">
          Разобрать пакет
        </b-button>
        <small v-if="!canManage" class="text-muted ml-1">Импорт доступен только с правом на изменение вопросов</small>
      </b-form>
    </b-card>

    <!-- Загрузка и разбор -->
    <b-card v-else-if="isPreparing" class="text-center py-3">
      <template v-if="uploadProgress > 0 && uploadProgress < 100">
        <div class="mb-1">Загрузка файла: {{ uploadProgress }}%</div>
        <b-progress :value="uploadProgress" :max="100" class="mx-auto progress-narrow" />
      </template>
      <template v-else>
        <b-spinner class="mb-1" />
        <div>Разбор пакета и авторазметка…</div>
      </template>
    </b-card>

    <template v-else>
      <!-- Шапка -->
      <b-card no-body class="mb-1">
        <b-card-body class="py-1">
          <div class="d-flex align-items-center flex-wrap header-row">
            <b-form-input
              v-model="draft.name"
              class="header-name mr-1 mb-50"
              placeholder="Название пакета"
              :state="draft.name && draft.name.trim() ? null : false"
            />
            <b-form-select
              :value="draft.kind"
              :options="kindOptions"
              class="header-select mr-1 mb-50"
              @change="onKindChange"
            />
            <b-form-radio-group
              :checked="draft.content_kind"
              :options="contentKindOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
              class="mr-1 mb-50"
              :disabled="isRetagging"
              @change="onContentKindChange"
            />
            <v-select
              :value="draft.planfix_pack_id"
              :options="planfixOptions"
              :reduce="pack => pack.id"
              :selectable="pack => pack.id !== null"
              :get-option-label="planfixOptionLabel"
              :loading="planfixPacksLoading || isChangingPlanfixPack"
              :disabled="!canViewGames || isChangingPlanfixPack"
              :placeholder="canViewGames ? 'ПФ-пакет не выбран' : 'нужен доступ к разделу Игры'"
              class="header-planfix mr-1 mb-50"
              @input="onPlanfixPackChange"
            >
              <template #option="pack">
                {{ planfixOptionLabel(pack) }}
                <small v-if="pack.id === null" class="text-muted ml-50">нет файла</small>
              </template>
              <template #no-options>Ничего не найдено</template>
            </v-select>
            <b-spinner v-if="isRetagging" small class="mb-50" title="Авторазметка" />
          </div>

          <div class="d-flex align-items-center flex-wrap mt-50">
            <span class="mr-2 mb-50">Премьера: <b>{{ formatDate(draft.dates && draft.dates.premiere_at) }}</b></span>
            <span class="mr-2 mb-50">
              Последний отыгрыш: <b>{{ formatDate(draft.dates && draft.dates.last_played_at) }}</b>
              <small class="text-muted">({{ playsCountText }})</small>
            </span>
            <span v-if="summaryParts.length" class="mr-2 mb-50 text-warning">⚠ {{ summaryParts.join(' · ') }}</span>
            <b-button
              variant="primary"
              class="ml-auto mb-50"
              :disabled="!canImport"
              :title="importDisabledReason"
              @click="confirmCommit"
            >
              <b-spinner v-if="isCommitting" small class="mr-50" />
              Импортировать
            </b-button>
          </div>

          <b-alert :show="Boolean(draft.blocked_reason)" variant="danger" class="mb-0 mt-50 p-1">
            {{ draft.blocked_reason }}
          </b-alert>
          <b-alert :show="!draft.blocked_reason && Boolean(draft.existing_game_id)" variant="warning" class="mb-0 mt-50 p-1">
            Пакет уже импортирован (игра #{{ draft.existing_game_id }}) — будет обновление.
            <span v-if="draft.removed_questions && draft.removed_questions.length">
              Из пакета пропало вопросов: {{ draft.removed_questions.length }} — они будут удалены.
            </span>
          </b-alert>
        </b-card-body>
      </b-card>

      <b-row>
        <!-- Дерево раундов и тем -->
        <b-col lg="3" class="mb-1">
          <b-card no-body class="tree-card">
            <b-card-body>
              <pack-import-tree
                :rounds="draft.rounds"
                :selected="selected"
                :mechanics-by-id="mechanicsById"
                :issues-by-theme="issuesByTheme"
                @select="selected = $event"
              />
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Выбранная тема -->
        <b-col lg="9">
          <b-card v-if="selectedTheme" no-body>
            <b-card-body>
              <div class="d-flex align-items-center flex-wrap mb-1">
                <h4 class="mb-50 mr-1">{{ selectedTheme.name || 'Без названия' }}</h4>
                <span class="mr-50 mb-50">Механика темы:</span>
                <b-form-select
                  :value="selectedTheme.mechanic_id"
                  :options="themeMechanicSelectOptions"
                  size="sm"
                  class="theme-mechanic-select mr-1 mb-50"
                  @change="onThemeMechanicChange(selected.roundIndex, selected.themeIndex, $event)"
                />
                <b-button
                  v-if="suggestedMechanic"
                  variant="flat-primary"
                  size="sm"
                  class="mb-50"
                  @click="onThemeMechanicChange(selected.roundIndex, selected.themeIndex, suggestedMechanic.id)"
                >
                  Применить «{{ suggestedMechanic.name }}»
                </b-button>
              </div>

              <pack-import-question-card
                v-for="question in selectedTheme.questions"
                :key="question.source_path"
                :question="question"
                :theme-mechanic-id="selectedTheme.mechanic_id"
                :mechanic-options="mechanicOptions"
                :slots="questionSlots(question, selectedTheme)"
                :lookups="lookups"
                :unresolved-count="unresolvedCount(question)"
                @mechanic="onQuestionMechanicChange(question, selectedTheme, $event)"
                @field="onQuestionField(question, $event)"
                @bindings="onQuestionBindings(question, $event)"
                @resolve="onQuestionResolve(question, $event)"
              />
              <div v-if="!selectedTheme.questions.length" class="text-muted">В теме нет вопросов</div>
            </b-card-body>
          </b-card>

          <!-- Удаляемые при повторном импорте -->
          <b-card v-if="draft.removed_questions && draft.removed_questions.length" title="Будут удалены">
            <b-list-group flush>
              <b-list-group-item
                v-for="removed in draft.removed_questions"
                :key="removed.id"
                class="d-flex px-0"
              >
                <span class="text-muted mr-1">#{{ removed.id }}</span>
                <span class="mr-1">{{ removed.answer }}</span>
                <small class="text-muted ml-auto">{{ removed.theme }}</small>
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import {
  BAlert, BButton, BCard, BCardBody, BCol, BForm, BFormFile, BFormGroup, BFormInput, BFormRadioGroup, BFormSelect,
  BListGroup, BListGroupItem, BProgress, BRow, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  computed, getCurrentInstance, onUnmounted, ref, watch,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import questionStorageStoreModule from '../questionStorageStoreModule'
import PackImportTree from './PackImportTree.vue'
import PackImportQuestionCard from './PackImportQuestionCard.vue'

const MODULE = 'question-storage'
const CONTENT_MUSIC = 'music'
const CONTENT_MOVIES = 'movies'
const CONTENT_ANY = 'any'
const MATCH_NEW = 'new'
const RETAG_DELAY_MS = 500
// Слот по умолчанию для механики без слотов — как MechanicSlots::slotsFor на бэкенде
const DEFAULT_SLOT_CODE = 'answer'

const pluralize = (count, one, few, many) => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few

  return many
}

// PHP отдаёт пустой ассоциативный массив как [] — приводим к объекту
const asObject = value => (value && !Array.isArray(value) && typeof value === 'object' ? value : {})

const isUnresolvedItem = item => item.id === null || item.id === undefined

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BForm,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BFormSelect,
    BListGroup,
    BListGroupItem,
    BProgress,
    BRow,
    BSpinner,
    vSelect,
    PackImportTree,
    PackImportQuestionCard,
  },
  setup(props, { root }) {
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, questionStorageStoreModule)

    const toast = useToast()
    const instance = getCurrentInstance().proxy
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })
    const showSuccess = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'CheckIcon', variant: 'success' },
    })
    const errorMessage = (error, fallback) => (error && error.response && error.response.data && error.response.data.message) || fallback

    const canManage = computed(() => instance.$can('manage', 'questions'))

    const kindOptions = [
      { value: 'svoya_igra', text: 'Своя игра' },
      { value: 'quiz', text: 'Квиз' },
    ]
    const contentKindOptions = [
      { value: CONTENT_MUSIC, text: 'Музыка' },
      { value: CONTENT_MOVIES, text: 'Кино' },
    ]

    // --- Справочники ---
    const mechanics = ref([])
    const lookups = ref({})
    const planfixPacks = ref([])
    const planfixPacksLoading = ref(false)

    // Сырые механики (без подстановки дефолтных слотов): дефолт зависит от вида контента пакета
    store.dispatch(`${MODULE}/fetchMechanics`)
      .then(response => { mechanics.value = response.data.data || [] })
      .catch(() => showError('Не удалось загрузить механики'))
    store.dispatch(`${MODULE}/fetchLookups`)
      .then(response => { lookups.value = response.data.data || {} })
      .catch(() => showError('Не удалось загрузить справочники'))
    // GET /planfix-packs закрыт правом на раздел «Игры» — без него не запрашиваем, селект выключен
    const canViewGames = instance.$can('view', 'games')
    if (canViewGames) {
      planfixPacksLoading.value = true
      store.dispatch(`${MODULE}/fetchPlanfixPacks`)
        .then(response => { planfixPacks.value = response.data.packs || [] })
        .catch(() => showError('Не удалось загрузить ПФ-пакеты'))
        .finally(() => { planfixPacksLoading.value = false })
    }

    const mechanicsById = computed(() => {
      const result = {}
      mechanics.value.forEach(mechanic => { result[mechanic.id] = mechanic })

      return result
    })

    // --- Черновик ---
    const token = ref(null)
    const draft = ref(null)
    const selected = ref(null)
    const siqFile = ref(null)
    const uploadProgress = ref(0)
    const isPreparing = ref(false)
    const prepareError = ref('')
    const isRetagging = ref(false)
    const isCommitting = ref(false)
    const isChangingPlanfixPack = ref(false)

    // Состояние отложенной авторазметки — объявлено до startFromRoute(), который его сбрасывает
    let retagTimer = null
    const retagScheduled = ref(false)
    const pendingRetagPaths = new Set()
    let retagRequestNumber = 0
    // source_path → номер последнего запроса, который должен перезаписать его разметку
    const retagRequestByPath = {}

    const normalizeQuestion = question => ({
      ...question,
      bindings: asObject(question.bindings),
      slot_values: asObject(question.slot_values),
      unresolved: question.unresolved || [],
      decades: question.decades || [],
      cluster_ids: question.cluster_ids || [],
      origin: question.origin || null,
    })

    const normalizeDraft = rawDraft => ({
      ...rawDraft,
      removed_questions: rawDraft.removed_questions || [],
      rounds: (rawDraft.rounds || []).map(round => ({
        ...round,
        themes: (round.themes || []).map(theme => ({
          ...theme,
          questions: (theme.questions || []).map(normalizeQuestion),
        })),
      })),
    })

    const firstThemePosition = loadedDraft => {
      const roundIndex = loadedDraft.rounds.findIndex(round => round.themes.length > 0)

      return roundIndex === -1 ? null : { roundIndex, themeIndex: 0 }
    }

    const applyPrepared = response => {
      token.value = response.data.token
      draft.value = normalizeDraft(response.data.draft)
      selected.value = firstThemePosition(draft.value)
    }

    const handlePrepareError = error => {
      // 413 отдаёт nginx/PHP раньше Laravel — тела с message нет
      prepareError.value = error && error.response && error.response.status === 413
        ? 'Файл слишком большой для сервера (лимит nginx/PHP)'
        : errorMessage(error, 'Не удалось разобрать пакет')
      showError(prepareError.value)
    }

    const prepareFromPlanfix = planfixPackId => {
      isPreparing.value = true
      prepareError.value = ''
      uploadProgress.value = 0
      store.dispatch(`${MODULE}/prepareImport`, { planfix_pack_id: planfixPackId })
        .then(applyPrepared)
        .catch(handlePrepareError)
        .finally(() => { isPreparing.value = false })
    }

    const uploadFile = () => {
      if (!siqFile.value) return
      const formData = new FormData()
      formData.append('file', siqFile.value)
      isPreparing.value = true
      prepareError.value = ''
      uploadProgress.value = 0
      store.dispatch(`${MODULE}/uploadImport`, {
        formData,
        onUploadProgress: event => {
          if (event.total) uploadProgress.value = Math.round((event.loaded * 100) / event.total)
        },
      })
        .then(applyPrepared)
        .catch(handlePrepareError)
        .finally(() => {
          isPreparing.value = false
          uploadProgress.value = 0
        })
    }

    const resetDraft = () => {
      clearTimeout(retagTimer)
      pendingRetagPaths.clear()
      retagScheduled.value = false
      token.value = null
      draft.value = null
      selected.value = null
      prepareError.value = ''
    }

    const startFromRoute = () => {
      resetDraft()
      const planfixPackId = parseInt(root.$route.query.planfix_pack_id, 10)
      if (planfixPackId > 0) prepareFromPlanfix(planfixPackId)
    }
    startFromRoute()
    // Переход с этой же страницы на другой ПФ-пакет (или к загрузке файла) — новый черновик
    watch(() => root.$route.query.planfix_pack_id, () => {
      if (root.$route.name === 'question-storage-import') startFromRoute()
    })

    // --- Механики и слоты ---
    const usedMechanicIds = computed(() => {
      const ids = new Set()
      if (!draft.value) return ids
      draft.value.rounds.forEach(round => round.themes.forEach(theme => {
        if (theme.mechanic_id) ids.add(theme.mechanic_id)
        theme.questions.forEach(question => { if (question.mechanic_id) ids.add(question.mechanic_id) })
      }))

      return ids
    })

    // Механики под вид контента пакета; архивные и чужие — только если уже выбраны
    const mechanicOptions = computed(() => {
      const contentKind = draft.value ? draft.value.content_kind : CONTENT_MUSIC

      return mechanics.value.filter(mechanic => usedMechanicIds.value.has(mechanic.id)
        || (!mechanic.is_archived && [contentKind, CONTENT_ANY].includes(mechanic.content_kind)))
    })

    const themeMechanicSelectOptions = computed(() => [
      { value: null, text: '— без механики —' },
      ...mechanicOptions.value.map(mechanic => ({ value: mechanic.id, text: mechanic.name })),
    ])

    const effectiveMechanicId = (question, theme) => {
      const mechanicId = question.mechanic_id !== null && question.mechanic_id !== undefined ? question.mechanic_id : theme.mechanic_id

      return mechanicId === undefined ? null : mechanicId
    }

    const defaultSlots = () => [{
      code: DEFAULT_SLOT_CODE,
      name: 'Ответ',
      entity: draft.value && draft.value.content_kind === CONTENT_MOVIES ? 'movie' : 'artist',
      multiple: true,
      required: false,
      counts_as_played: true,
    }]

    // Слоты механики вопроса; привязки в слотах вне механики тоже показываем, чтобы их было видно и можно убрать
    const questionSlots = (question, theme) => {
      const mechanic = mechanicsById.value[effectiveMechanicId(question, theme)]
      const slots = mechanic && Array.isArray(mechanic.slots) && mechanic.slots.length ? [...mechanic.slots] : defaultSlots()
      const codes = new Set(slots.map(slot => slot.code))
      Object.entries(question.bindings).forEach(([code, items]) => {
        if (codes.has(code) || !items || !items.length) return
        slots.push({
          code, name: code, entity: items[0].entity, multiple: true, required: false, counts_as_played: true,
        })
        codes.add(code)
      })

      return slots
    }

    const selectedTheme = computed(() => {
      if (!draft.value || !selected.value) return null
      const round = draft.value.rounds[selected.value.roundIndex]

      return round ? round.themes[selected.value.themeIndex] || null : null
    })

    const suggestedMechanic = computed(() => {
      const theme = selectedTheme.value
      if (!theme || !theme.suggested_mechanic_id || theme.suggested_mechanic_id === theme.mechanic_id) return null

      return mechanicsById.value[theme.suggested_mechanic_id] || null
    })

    // --- Привязки и сводка ---
    const unresolvedKey = item => `${item.entity}|${item.name}`

    // Красные чипы без id + не найденные сущности, для которых чипа нет; на сохранении бэкенд их отбросит
    const unresolvedCount = question => {
      const chipKeys = new Set()
      let count = 0
      Object.values(question.bindings).forEach(items => (items || []).forEach(item => {
        if (item.entity !== 'text' && isUnresolvedItem(item)) {
          count += 1
          chipKeys.add(unresolvedKey(item))
        }
      }))

      return count + question.unresolved.filter(entry => !chipKeys.has(unresolvedKey(entry))).length
    }

    const hasBindings = question => Object.values(question.bindings)
      .some(items => (items || []).some(item => !isUnresolvedItem(item)))
      || Object.values(question.slot_values).some(text => String(text || '').trim() !== '')

    const issuesByTheme = computed(() => {
      const result = {}
      if (!draft.value) return result
      draft.value.rounds.forEach((round, roundIndex) => round.themes.forEach((theme, themeIndex) => {
        result[`${roundIndex}-${themeIndex}`] = theme.questions.some(question => effectiveMechanicId(question, theme) === null
          || !hasBindings(question)
          || unresolvedCount(question) > 0)
      }))

      return result
    })

    const summaryParts = computed(() => {
      if (!draft.value) return []
      let withoutBindings = 0
      let withUnresolved = 0
      let themesWithoutMechanic = 0
      draft.value.rounds.forEach(round => round.themes.forEach(theme => {
        if (theme.mechanic_id === null || theme.mechanic_id === undefined) themesWithoutMechanic += 1
        theme.questions.forEach(question => {
          if (!hasBindings(question)) withoutBindings += 1
          if (unresolvedCount(question) > 0) withUnresolved += 1
        })
      }))

      const parts = []
      if (withoutBindings) parts.push(`${withoutBindings} ${pluralize(withoutBindings, 'вопрос', 'вопроса', 'вопросов')} без привязок`)
      if (withUnresolved) parts.push(`${withUnresolved} ${pluralize(withUnresolved, 'вопрос', 'вопроса', 'вопросов')} с непривязанными`)
      if (themesWithoutMechanic) parts.push(`${themesWithoutMechanic} ${pluralize(themesWithoutMechanic, 'тема', 'темы', 'тем')} без механики`)

      return parts
    })

    // --- Авторазметка (retag) ---
    const forEachQuestion = callback => {
      draft.value.rounds.forEach(round => round.themes.forEach(theme => theme.questions.forEach(question => callback(question, theme))))
    }

    // Механики всех новых вопросов: бэкенд механики черновика не хранит, берёт их отсюда
    const mechanicsMap = () => {
      const map = {}
      forEachQuestion((question, theme) => {
        if (question.match === MATCH_NEW) map[question.source_path] = effectiveMechanicId(question, theme)
      })

      return map
    }

    // paths: какие вопросы перезаписать ответом; null — все новые (смена вида контента)
    const runRetag = (paths, contentKind) => {
      retagRequestNumber += 1
      const requestNumber = retagRequestNumber
      const targetPaths = paths || Object.keys(mechanicsMap())
      targetPaths.forEach(path => { retagRequestByPath[path] = requestNumber })
      isRetagging.value = true
      const requestToken = token.value

      return store.dispatch(`${MODULE}/retagImport`, {
        token: requestToken,
        data: { content_kind: contentKind, mechanics: mechanicsMap() },
      })
        .then(response => {
          // Черновик могли сбросить (переход на другой пакет), пока шёл запрос
          if (!draft.value || token.value !== requestToken) return
          const serverQuestions = {}
          response.data.draft.rounds.forEach(round => round.themes.forEach(theme => theme.questions.forEach(question => {
            serverQuestions[question.source_path] = question
          })))
          // Перезаписываем только серверную разметку новых вопросов; механики, ответы и комментарии — локальные
          forEachQuestion(question => {
            const serverQuestion = serverQuestions[question.source_path]
            if (question.match !== MATCH_NEW || !serverQuestion || retagRequestByPath[question.source_path] !== requestNumber) return
            const normalized = normalizeQuestion(serverQuestion)
            question.bindings = normalized.bindings
            question.unresolved = normalized.unresolved
            question.decades = normalized.decades
            question.cluster_ids = normalized.cluster_ids
            question.origin = normalized.origin
          })
        })
        .finally(() => {
          if (requestNumber === retagRequestNumber) isRetagging.value = false
        })
    }

    const scheduleRetag = paths => {
      paths.forEach(path => pendingRetagPaths.add(path))
      if (!pendingRetagPaths.size) return
      clearTimeout(retagTimer)
      retagScheduled.value = true
      retagTimer = setTimeout(() => {
        const batch = [...pendingRetagPaths]
        pendingRetagPaths.clear()
        retagScheduled.value = false
        runRetag(batch, draft.value.content_kind)
          .catch(error => showError(errorMessage(error, 'Не удалось обновить авторазметку')))
      }, RETAG_DELAY_MS)
    }

    const onContentKindChange = contentKind => {
      if (!draft.value || contentKind === draft.value.content_kind) return
      const previous = draft.value.content_kind
      draft.value.content_kind = contentKind
      // Смена вида перекрывает отложенную перезапись по механикам: перезаписываются все новые вопросы
      clearTimeout(retagTimer)
      pendingRetagPaths.clear()
      retagScheduled.value = false
      runRetag(null, contentKind)
        .catch(error => {
          draft.value.content_kind = previous
          showError(errorMessage(error, 'Не удалось перезапустить авторазметку'))
        })
    }

    // Механика темы действует на вопросы без своей механики — им и нужна новая разметка
    const onThemeMechanicChange = (roundIndex, themeIndex, mechanicId) => {
      const theme = draft.value.rounds[roundIndex].themes[themeIndex]
      if (theme.mechanic_id === mechanicId) return
      theme.mechanic_id = mechanicId
      scheduleRetag(theme.questions
        .filter(question => question.match === MATCH_NEW && (question.mechanic_id === null || question.mechanic_id === undefined))
        .map(question => question.source_path))
    }

    const onQuestionMechanicChange = (question, theme, mechanicId) => {
      const before = effectiveMechanicId(question, theme)
      question.mechanic_id = mechanicId
      if (question.match === MATCH_NEW && effectiveMechanicId(question, theme) !== before) {
        scheduleRetag([question.source_path])
      }
    }

    // Тип шапки — для новых вопросов; совпавшие сохраняют свой тип из БД
    const onKindChange = kind => {
      draft.value.kind = kind
      forEachQuestion(question => {
        if (question.match === MATCH_NEW) question.kind = kind
      })
    }

    const onQuestionField = (question, { field, value }) => {
      question[field] = value
    }

    // Убранный или заменённый красный чип больше не «не найден»: чистим его из unresolved
    const onQuestionBindings = (question, value) => {
      const nextKeys = new Set()
      Object.values(asObject(value.bindings)).forEach(items => (items || []).forEach(item => {
        if (isUnresolvedItem(item)) nextKeys.add(unresolvedKey(item))
      }))
      const removedKeys = new Set()
      Object.values(question.bindings).forEach(items => (items || []).forEach(item => {
        if (isUnresolvedItem(item) && !nextKeys.has(unresolvedKey(item))) removedKeys.add(unresolvedKey(item))
      }))
      if (removedKeys.size) {
        question.unresolved = question.unresolved.filter(entry => !removedKeys.has(unresolvedKey(entry)))
      }

      question.bindings = asObject(value.bindings)
      question.decades = value.decades || []
      question.cluster_ids = value.cluster_ids || []
      question.origin = value.origin || null
      question.slot_values = asObject(value.slot_values)
    }

    const onQuestionResolve = (question, entry) => {
      const index = question.unresolved.findIndex(candidate => unresolvedKey(candidate) === unresolvedKey(entry))
      if (index !== -1) question.unresolved = question.unresolved.filter((candidate, candidateIndex) => candidateIndex !== index)
    }

    // --- ПФ-пакет ---
    const planfixOptions = computed(() => {
      const options = planfixPacks.value
      const currentId = draft.value ? draft.value.planfix_pack_id : null
      if (currentId && !options.some(pack => pack.id === currentId)) {
        return [{ id: currentId, key: null, name: `ПФ-пакет #${currentId}` }, ...options]
      }

      return options
    })
    const planfixOptionLabel = pack => (pack.key ? `${pack.key} · ${pack.name || 'Без названия'}` : pack.name)

    // Локальные правки, которые переносятся в пересобранный черновик для вопросов, оставшихся новыми
    const LOCAL_QUESTION_FIELDS = [
      'kind', 'mechanic_id', 'answer', 'answer_comment', 'bindings', 'unresolved', 'decades', 'cluster_ids', 'origin', 'slot_values',
    ]

    // Пересобранный бэкендом черновик + локальные правки: шапка, механики тем, вопросы, которые и были, и остались новыми
    const mergeRebuiltDraft = (localDraft, serverDraft) => {
      const rebuilt = normalizeDraft(serverDraft)
      const localQuestions = {}
      localDraft.rounds.forEach(round => round.themes.forEach(theme => theme.questions.forEach(question => {
        localQuestions[question.source_path] = question
      })))

      rebuilt.name = localDraft.name
      rebuilt.kind = localDraft.kind
      rebuilt.content_kind = localDraft.content_kind
      rebuilt.rounds.forEach((round, roundIndex) => round.themes.forEach((theme, themeIndex) => {
        const localRound = localDraft.rounds[roundIndex]
        const localTheme = localRound ? localRound.themes[themeIndex] : null
        // Механика темы из игры повторного импорта важнее; иначе — выбранная пользователем
        if ((theme.mechanic_id === null || theme.mechanic_id === undefined) && localTheme) {
          theme.mechanic_id = localTheme.mechanic_id === undefined ? null : localTheme.mechanic_id
        }
        theme.questions.forEach(question => {
          const localQuestion = localQuestions[question.source_path]
          if (question.match !== MATCH_NEW || !localQuestion || localQuestion.match !== MATCH_NEW) return
          LOCAL_QUESTION_FIELDS.forEach(field => { question[field] = localQuestion[field] })
        })
      }))

      return rebuilt
    }

    // Смена ПФ-пакета в шапке: бэкенд пересчитывает даты и повторный импорт (пакет может уже иметь игру)
    const onPlanfixPackChange = planfixPackId => {
      const nextId = planfixPackId || null
      const previousId = draft.value.planfix_pack_id || null
      if (nextId === previousId) return
      draft.value.planfix_pack_id = nextId
      isChangingPlanfixPack.value = true
      const requestToken = token.value

      store.dispatch(`${MODULE}/changeImportPlanfixPack`, { token: requestToken, planfixPackId: nextId })
        .then(response => {
          if (!draft.value || token.value !== requestToken) return
          draft.value = mergeRebuiltDraft(draft.value, response.data.draft)
          if (!selected.value) selected.value = firstThemePosition(draft.value)
        })
        .catch(error => {
          if (draft.value && token.value === requestToken) draft.value.planfix_pack_id = previousId
          showError(errorMessage(error, 'Не удалось сменить ПФ-пакет'))
        })
        .finally(() => { isChangingPlanfixPack.value = false })
    }

    // --- Даты ---
    const formatDate = value => {
      if (!value) return '—'
      const [year, month, day] = String(value).slice(0, 10).split('-')

      return `${day}.${month}.${year}`
    }

    const playsCountText = computed(() => {
      const count = (draft.value && draft.value.dates && draft.value.dates.plays_count) || 0

      return `${count} ${pluralize(count, 'игра', 'игры', 'игр')}`
    })

    // --- Коммит ---
    const blankAnswersCount = computed(() => {
      let count = 0
      if (draft.value) forEachQuestion(question => { if (String(question.answer || '').trim() === '') count += 1 })

      return count
    })

    const importDisabledReason = computed(() => {
      if (!draft.value) return ''
      if (!canManage.value) return 'Нет права на изменение вопросов'
      if (draft.value.blocked_reason) return draft.value.blocked_reason
      if (!draft.value.name || !draft.value.name.trim()) return 'Укажите название пакета'
      if (blankAnswersCount.value) return `Пустой ответ в ${blankAnswersCount.value} ${pluralize(blankAnswersCount.value, 'вопросе', 'вопросах', 'вопросах')}`
      if (isRetagging.value || retagScheduled.value) return 'Идёт авторазметка'
      if (isChangingPlanfixPack.value) return 'Обновляется ПФ-пакет'
      if (isCommitting.value) return 'Импорт выполняется'

      return ''
    })
    const canImport = computed(() => Boolean(draft.value) && importDisabledReason.value === '')

    // Только редактируемые поля (QuestionImportService::mergeClientDraft); серверные бэкенд берёт из своего черновика
    const buildCommitPayload = () => ({
      name: draft.value.name.trim(),
      kind: draft.value.kind,
      content_kind: draft.value.content_kind,
      planfix_pack_id: draft.value.planfix_pack_id || null,
      rounds: draft.value.rounds.map(round => ({
        themes: round.themes.map(theme => ({
          mechanic_id: theme.mechanic_id === undefined ? null : theme.mechanic_id,
          questions: theme.questions.map(question => {
            const payload = {
              source_path: question.source_path,
              // Явный тип у каждого вопроса: иначе бэкенд проставит тип шапки и совпавшим (mergeClientDraft)
              kind: question.match === MATCH_NEW ? draft.value.kind : (question.kind || draft.value.kind),
              mechanic_id: question.mechanic_id === undefined ? null : question.mechanic_id,
              answer_comment: String(question.answer_comment || '').trim() === '' ? null : question.answer_comment,
              bindings: question.bindings,
              decades: question.decades,
              cluster_ids: question.cluster_ids,
              origin: question.origin,
              slot_values: question.slot_values,
            }
            // answer обязателен, если передан; пустые ответы блокируют импорт (importDisabledReason)
            if (String(question.answer || '').trim() !== '') payload.answer = question.answer

            return payload
          }),
        })),
      })),
    })

    const commit = () => {
      isCommitting.value = true
      store.dispatch(`${MODULE}/commitImport`, { token: token.value, draft: buildCommitPayload() })
        .then(response => {
          showSuccess('Пакет импортирован')
          root.$router.push({ name: 'question-storage-pack', params: { id: response.data.data.game_id } })
        })
        .catch(error => {
          if (error && error.response && error.response.status === 409) {
            // 409: коммит по токену уже идёт либо тот же файл успели импортировать
            showError(errorMessage(error, 'Импорт этого пакета уже выполняется'))
            return
          }
          showError(errorMessage(error, 'Не удалось импортировать пакет'))
        })
        .finally(() => { isCommitting.value = false })
    }

    const confirmCommit = async () => {
      if (!canImport.value) return
      const removedCount = draft.value.removed_questions.length
      if (removedCount) {
        const confirmed = await root.$bvModal.msgBoxConfirm(
          `При обновлении будет удалено ${removedCount} ${pluralize(removedCount, 'вопрос', 'вопроса', 'вопросов')}, которых больше нет в пакете. Продолжить?`,
          {
            title: 'Повторный импорт',
            okTitle: 'Импортировать',
            cancelTitle: 'Отмена',
            okVariant: 'danger',
            centered: true,
          },
        )
        if (!confirmed) return
      }
      commit()
    }

    // Модуль стора не снимаем: Vue 2 создаёт компонент следующего роута до уничтожения текущего, а он использует тот же модуль
    onUnmounted(() => {
      clearTimeout(retagTimer)
    })

    return {
      canManage,
      canViewGames,
      kindOptions,
      contentKindOptions,
      mechanicsById,
      lookups,
      planfixPacksLoading,
      draft,
      selected,
      siqFile,
      uploadProgress,
      isPreparing,
      prepareError,
      isRetagging,
      isCommitting,
      isChangingPlanfixPack,
      onPlanfixPackChange,
      uploadFile,
      mechanicOptions,
      themeMechanicSelectOptions,
      questionSlots,
      selectedTheme,
      suggestedMechanic,
      unresolvedCount,
      issuesByTheme,
      summaryParts,
      onContentKindChange,
      onKindChange,
      onThemeMechanicChange,
      onQuestionMechanicChange,
      onQuestionField,
      onQuestionBindings,
      onQuestionResolve,
      planfixOptions,
      planfixOptionLabel,
      formatDate,
      playsCountText,
      importDisabledReason,
      canImport,
      confirmCommit,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.header-name {
  max-width: 360px;
  font-weight: 600;
}
.header-select {
  max-width: 150px;
}
.header-planfix {
  min-width: 280px;
}
.theme-mechanic-select {
  max-width: 280px;
}
.progress-narrow {
  max-width: 400px;
}
.tree-card {
  position: sticky;
  top: 90px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}
</style>
